import { z } from "zod";
import { memberIdSchema, parameterValueSchema, standardParameterReferenceSchema } from "./common.schemas";
import { newCanonicalSchema } from "./entity-id.schemas";

const diagnosticComponentSchema = z.object({
  id: memberIdSchema.optional(),
  label: z.string().trim().min(1),
  value: parameterValueSchema,
});

const diagnosticObservationSchema = z.object({
  id: memberIdSchema.optional(),
  label: z.string().trim().min(1),
  repeatable: z.boolean(),
  noteLabel: z.string().trim().min(1).optional(),
  components: z.array(diagnosticComponentSchema).min(1),
});

const diagnosticParameterSchema = z.object({
  id: memberIdSchema.optional(),
  label: z.string().trim().min(1),
  value: parameterValueSchema,
});

const deviceRequirementSchema = z.object({
  id: memberIdSchema.optional(),
  definition: z.string().url(),
});

const diagnosticDefinitionContentSchema = z.object({
  standardObservations: z.array(z.enum(["tolerance"])).default(["tolerance"]),
  procedureNoteLabel: z.string().trim().min(1).optional(),
  observations: z.array(diagnosticObservationSchema).min(1),
  parameters: z.array(z.union([diagnosticParameterSchema, standardParameterReferenceSchema])).refine((entries) => {
    const keys = entries.flatMap((entry) => ("kind" in entry ? [entry.key] : []));
    return new Set(keys).size === keys.length;
  }, "Standard parameters must be unique"),
  requirements: z.object({
    devices: z.array(deviceRequirementSchema),
    location: z.string().trim().min(1).optional(),
  }),
});

export const defineDiagnosticDefinitionInputSchema = diagnosticDefinitionContentSchema.extend({
  definitionUrl: newCanonicalSchema,
  name: z.string().trim().min(1),
  description: z.string().trim().min(1).optional(),
});
export type DefineDiagnosticDefinitionInput = z.infer<typeof defineDiagnosticDefinitionInputSchema>;

export const reviseDiagnosticDefinitionInputSchema = diagnosticDefinitionContentSchema.extend({
  definitionUrl: z.string().url(),
  expectedVersion: z.number().int().positive(),
});
export type ReviseDiagnosticDefinitionInput = z.infer<typeof reviseDiagnosticDefinitionInputSchema>;

export const correctDiagnosticDefinitionInputSchema = z.object({
  definitionUrl: z.string().url(),
  name: z.string().trim().min(1).optional(),
  description: z.string().trim().min(1).nullable().optional(),
});
export type CorrectDiagnosticDefinitionInput = z.infer<typeof correctDiagnosticDefinitionInputSchema>;

export const changeDiagnosticDefinitionAvailabilityInputSchema = z.object({
  definitionUrl: z.string().url(),
});
export type ChangeDiagnosticDefinitionAvailabilityInput = z.infer<
  typeof changeDiagnosticDefinitionAvailabilityInputSchema
>;
